import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { AlertIcon, CloseIcon, InfoIcon } from '../../Screens/ChatPage/Components/Icons';

interface ToastMessage {
   id: string;
   message: string;
   type: 'error' | 'success' | 'warning' | 'info';
   duration: number;
   timestamp: number;
}

let showToast: ((message: string, type?: ToastMessage['type'], duration?: number) => void) | undefined;

/** Banners raised before the toast host is mounted (e.g. from a page's first effects) are shown once it mounts. */
const pendingToasts: Array<{ message: string; type: ToastMessage['type']; duration: number }> = [];

const show = (message: string, type: ToastMessage['type'], duration: number) => {
   if (showToast) showToast(message, type, duration);
   else pendingToasts.push({ message, type, duration });
};

export const showErrorBanner = (message: string, duration = 4000) => show(message, 'error', duration);

export const showSuccessBanner = (message: string, duration = 3000) => show(message, 'success', duration);

export const showWarningBanner = (message: string, duration = 4000) => show(message, 'warning', duration);

export const showInfoBanner = (message: string, duration = 3000) => show(message, 'info', duration);

/**
 * A short notice over the screen, on the tokens: persimmon marks an error, blue everything else.
 * A drawn glyph carries the state as well as the colour.
 */
const ToastItem: React.FC<{ toast: ToastMessage; onRemove: (id: string) => void }> = ({ toast, onRemove }) => {
   const [isVisible, setIsVisible] = useState(false);
   const [isRemoving, setIsRemoving] = useState(false);

   useEffect(() => {
      const timer = setTimeout(() => setIsVisible(true), 20);
      return () => clearTimeout(timer);
   }, []);

   useEffect(() => {
      const timer = setTimeout(() => {
         setIsRemoving(true);
         setTimeout(() => onRemove(toast.id), 320);
      }, toast.duration);
      return () => clearTimeout(timer);
   }, [toast.duration, toast.id, onRemove]);

   const handleRemove = useCallback(() => {
      setIsRemoving(true);
      setTimeout(() => onRemove(toast.id), 320);
   }, [toast.id, onRemove]);

   const Icon = toast.type === 'error' || toast.type === 'warning' ? AlertIcon : InfoIcon;

   return (
      <div className={`cx-toast cx-frosted cx-frosted--thick cx-toast--${toast.type}${isVisible && !isRemoving ? ' cx-toast--visible' : ''}`} role={toast.type === 'error' ? 'alert' : 'status'}>
         <Icon />
         <p className="cx-toast__text">{toast.message}</p>
         <button type="button" className="cx-toast__close" onClick={handleRemove} aria-label="Dismiss">
            <CloseIcon />
         </button>
      </div>
   );
};

export const ErrorBannerToast = () => {
   const [toasts, setToasts] = useState<ToastMessage[]>([]);

   useEffect(() => {
      showToast = (message: string, type: ToastMessage['type'] = 'info', duration = 3000) => {
         const id = `toast-${Date.now()}-${Math.random()}`;
         setToasts((prev) => [...prev, { id, message, type, duration, timestamp: Date.now() }]);
      };
      pendingToasts.splice(0).forEach((toast) => showToast?.(toast.message, toast.type, toast.duration));

      return () => {
         showToast = () => {};
      };
   }, []);

   const removeToast = useCallback((id: string) => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
   }, []);

   if (toasts.length === 0) return null;

   return createPortal(
      <div className="cx-toasts">
         {toasts.map((toast) => (
            <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
         ))}
      </div>,
      document.body
   );
};
